import request from '@/utils/request'

/**
 * 提交作业
 * @param {Object} submitData 提交数据
 * @returns {Promise}
 */
export function submitHomework(submitData) {
  return request({
    url: '/homework-submit/submit',
    method: 'post',
    data: submitData
  })
}

/**
 * 根据学生ID获取提交记录
 * @param {Number} studentId 学生ID
 * @returns {Promise}
 */
export function getSubmitsByStudentId(studentId) {
  return request({
    url: `/homework-submit/student/${studentId}`,
    method: 'get'
  })
}

/**
 * 根据作业ID获取提交记录
 * @param {Number} homeworkId 作业ID
 * @returns {Promise}
 */
export function getSubmitsByHomeworkId(homeworkId) {
  return request({
    url: `/homework-submit/homework/${homeworkId}`,
    method: 'get'
  })
}

/**
 * 根据学生ID和作业ID获取提交记录
 * @param {Number} studentId 学生ID
 * @param {Number} homeworkId 作业ID
 * @returns {Promise}
 */
export function getSubmitByStudentIdAndHomeworkId(studentId, homeworkId) {
  return request({
    url: `/homework-submit/student/${studentId}/homework/${homeworkId}`,
    method: 'get'
  })
}

/**
 * 根据提交记录ID获取明细
 * @param {Number} submitId 提交记录ID
 * @returns {Promise}
 */
export function getSubmitDetails(submitId) {
  return request({
    url: `/homework-submit/${submitId}/details`,
    method: 'get'
  })
}

/**
 * 批改作业（教师功能）
 * @param {Number} submitId 提交记录ID
 * @param {Array} details 批改明细
 * @param {String} homeworkComment 整体作业评语
 * @returns {Promise}
 */
export function gradeHomework(submitId, details, homeworkComment = '') {
  return request({
    url: `/homework-submit/${submitId}/grade`,
    method: 'put',
    data: {
      details: details,
      homeworkComment: homeworkComment
    }
  })
}

/**
 * 获取学生错题记录
 * @param {Number} studentId 学生ID
 * @returns {Promise}
 */
export function getWrongQuestions(studentId) {
  return request({
    url: `/homework-submit/wrong-questions/student/${studentId}`,
    method: 'get'
  })
}

/**
 * 获取学生错题视图记录（包含题目/作业等丰富字段）
 * @param {Number} studentId 学生ID
 * @returns {Promise}
 */
export function getWrongQuestionsView(studentId) {
  return request({
    url: `/homework-submit/wrong-questions-view/student/${studentId}`,
    method: 'get'
  })
}

/**
 * 获取课程错题集（按课程ID获取所有错题，去重并统计错误人数）
 * @param {Number} courseId 课程ID
 * @param {Object} filters 筛选条件
 * @returns {Promise}
 */
export function getCourseWrongQuestions(courseId, filters = {}) {
  const params = {}

  // 添加筛选参数
  if (filters.homeworkId) params.homeworkId = filters.homeworkId
  if (filters.knowledge) params.knowledge = filters.knowledge
  if (filters.examId) params.examId = filters.examId
  if (filters.gradeId) params.gradeId = filters.gradeId
  if (filters.studentId) params.studentId = filters.studentId

  return request({
    url: `/homework-submit/wrong-questions-view/course/${courseId}`,
    method: 'get',
    params
  })
}

/**
 * 获取学生课程错题集（学生只能查看自己的错题）
 * @param {Number} courseId 课程ID
 * @param {Number} studentId 学生ID
 * @param {Object} filters 筛选条件
 * @returns {Promise}
 */
export function getStudentCourseWrongQuestions(courseId, studentId, filters = {}) {
  const params = {}

  // 添加筛选参数
  if (filters.homeworkId) params.homeworkId = filters.homeworkId
  if (filters.knowledge) params.knowledge = filters.knowledge

  return request({
    url: `/homework-submit/wrong-questions-view/course/${courseId}/student/${studentId}`,
    method: 'get',
    params
  })
}

/**
 * 获取学生课程错题集筛选选项
 * @param {Number} courseId 课程ID
 * @param {Number} studentId 学生ID
 * @returns {Promise}
 */
export function getStudentCourseWrongQuestionsFilterOptions(courseId, studentId) {
  return request({
    url: `/homework-submit/wrong-questions-view/course/${courseId}/student/${studentId}/filter-options`,
    method: 'get'
  })
}

/**
 * 获取课程错题集筛选选项
 * @param {Number} courseId 课程ID
 * @returns {Promise}
 */
export function getCourseWrongQuestionsFilterOptions(courseId) {
  return request({
    url: `/homework-submit/wrong-questions-view/course/${courseId}/filter-options`,
    method: 'get'
  })
}

/**
 * 上传作业图片
 * @param {FormData} formData 包含文件和参数的FormData
 * @returns {Promise}
 */
export function uploadHomeworkImage(formData) {
  return request({
    url: '/homework-submit/upload-image',
    method: 'post',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

/**
 * 重新上传作业图片
 * @param {FormData} formData 包含文件和参数的FormData
 * @returns {Promise}
 */
export function reuploadHomeworkImage(formData) {
  return request({
    url: '/homework-submit/reupload-image',
    method: 'post',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

/**
 * 退回作业（将已提交但未批改的作业退回到未提交状态）
 * @param {Number} studentId 学生ID
 * @param {Number} homeworkId 作业ID
 * @returns {Promise}
 */
export function returnHomework(studentId, homeworkId) {
  return request({
    url: `/homework-submit/return/${studentId}/${homeworkId}`,
    method: 'put'
  })
}

/**
 * 更新作业图片URL（支持多张图片）
 * @param {Number} homeworkId 作业ID
 * @param {Number} studentId 学生ID
 * @param {String} imageUrls 图片URL（单张图片为字符串，多张图片为JSON字符串）
 * @returns {Promise}
 */
export function updateHomeworkImageUrl(homeworkId, studentId, imageUrls) {
  return request({
    url: `/homework-submit/update-image-url`,
    method: 'post',
    params: {
      homeworkId,
      studentId,
      imageUrls
    }
  })
}
